import { useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from '@phosphor-icons/react/dist/csr/Eye';
import { UploadSimpleIcon } from '@phosphor-icons/react/dist/csr/UploadSimple';

import { Button } from '../../design-system/primitives/Button/Button';
import { Card } from '../../design-system/composites/Card/Card';
import { FileField } from '../../design-system/primitives/FileField/FileField';
import { ClientDataTable } from '../../design-system/composites/DataTable/ClientDataTable';
import {
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { centresImportApi, type ImportResult, type ImportRow } from '../../lib/api/reference';
import styles from './ReferenceImportScreen.module.css';

/**
 * The centre CSV import — the two-call dry-run/apply shape
 * `CentreController::import()` implements (spec §5), replacing the legacy
 * `uploadCenter.php`.
 *
 * ## `file` travels as text, not as a `multipart/form-data` upload
 *
 * `ImportCentresRequest` reads `file` as a JSON STRING (that class's own
 * docblock: "this API has no other file-upload endpoint... a raw CSV string
 * in the same JSON body keeps this operation on that one wire format"). This
 * screen therefore reads the chosen `File` with `.text()` and holds the
 * result in state — never a `FormData`, never a second `Content-Type`.
 *
 * ## The four properties this screen exists to satisfy
 *
 * 1. **Nothing is written by a preview.** `handlePreview` calls
 *    `centresImportApi.preview`, never `.apply` — the dry run's own endpoint
 *    contract (no `preview_token` sent).
 * 2. **Every row's verdict renders.** `preview.rows` — create/update/
 *    unchanged/reject, with `reason` for a reject — populates the table the
 *    moment the dry run resolves, before Apply can do anything at all.
 * 3. **Apply is unreachable without a preview OF THE CURRENT FILE.** The
 *    button is rendered from the first frame (not conditionally mounted) so
 *    a mutant that quietly enables it has something concrete to fail against
 *    — see `disabled` below — and it stays disabled until `preview` exists
 *    AND was taken against the exact text now in `fileText`. Choosing a
 *    DIFFERENT file after a preview invalidates it immediately, client-side,
 *    without waiting on the server's own `preview_token` mismatch (a 422)
 *    to say so.
 * 4. **A stale or mismatched token is still the server's call.** The server
 *    re-parses the re-uploaded file and compares its own freshly computed
 *    token to the one sent (`CentreController::import()`'s own docblock) —
 *    this screen's client-side guard in point 3 is a courtesy that saves a
 *    round trip for the common case (the file visibly changed), not a
 *    replacement for that check.
 */

export interface ReferenceImportScreenProps {
  permissions: readonly string[];
}

interface VerdictRow extends Row, ImportRow {}

function toRow(row: ImportRow): VerdictRow {
  return { ...row, id: String(row.row_number) };
}

const FORBIDDEN_CODE = 'forbidden';

export function ReferenceImportScreen({ permissions }: ReferenceImportScreenProps) {
  const { t } = useTranslation();
  const canManage = permissions.includes('reference.manage');

  const [fileName, setFileName] = useState<string | null>(null);
  const [fileText, setFileText] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [previewedFileText, setPreviewedFileText] = useState<string | null>(null);
  const [previewFailure, setPreviewFailure] = useState<string | null>(null);
  const [previewReference, setPreviewReference] = useState<string | null>(null);
  const [applyFailure, setApplyFailure] = useState<string | null>(null);
  const [applyReference, setApplyReference] = useState<string | null>(null);
  const [applied, setApplied] = useState<ImportResult | null>(null);

  const inFlight = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // `fallback` is the sentence to use when this is neither `forbidden` nor
  // one of the three transport-level codes below NOR a validation response
  // with a usable `details` sentence — the preview and apply calls each pass
  // their own, because "the import may or may not have run" is true only
  // for the apply path (see the apply call site).
  function describeFailure(error: ApiError, fallback: string): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('reference.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('reference.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default: {
        // Both the dry-run's own "unreadable file" 422 and the apply's
        // "preview_token mismatch"/"row failed" 422s carry a full,
        // server-localised sentence in `details` — the same reasoning
        // `ReferenceLifecycleActions.tsx` applies to a 409's
        // `details.children[0]`. Rendered as-is rather than replaced by a
        // fixed client-side sentence that could only ever be vaguer.
        const [firstField] = Object.values(error.details);
        return firstField?.[0] ?? fallback;
      }
    }
  }

  function renderFailure(
    failureText: string | null,
    referenceText: string | null,
    referenceTestId: string,
  ): ReactNode {
    return (
      <div className={styles['failure']} role="alert" aria-atomic="true">
        {failureText === null ? null : <span>{failureText}</span>}
        {referenceText === null ? null : (
          <span className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid={referenceTestId}>
              {referenceText}
            </bdi>
          </span>
        )}
      </div>
    );
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const chosen = event.target.files?.[0];
    if (chosen === undefined) return;

    setFileName(chosen.name);
    // Cleared synchronously, before the `await` below — not after it
    // resolves. Otherwise `fileName` names the newly chosen file while
    // `fileText`/`previewedFileText` still hold the PREVIOUS file's text for
    // the whole span of the read, and if the read itself rejects (the file
    // moved or got locked between the picker and this line — ordinary on
    // Windows) that stale text would never get cleared at all.
    setFileText(null);
    setPreviewedFileText(null);
    setPreviewFailure(null);
    setPreviewReference(null);
    setApplyFailure(null);
    setApplyReference(null);
    setApplied(null);

    try {
      const text = await chosen.text();
      setFileText(text);
    } catch {
      setPreviewFailure(t('reference.import.fileReadFailed'));
    }
  }

  async function handlePreview(): Promise<void> {
    if (inFlight.current || fileText === null) return;
    inFlight.current = true;
    setPreviewing(true);
    setPreviewFailure(null);
    setPreviewReference(null);
    setApplyFailure(null);
    setApplyReference(null);
    setApplied(null);

    try {
      const result = await centresImportApi.preview(fileText);
      setPreview(result);
      setPreviewedFileText(fileText);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setPreviewFailure(describeFailure(error, t('reference.import.previewFailed')));
      setPreviewReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setPreviewing(false);
    }
  }

  async function handleApply(): Promise<void> {
    if (inFlight.current || preview === null || fileText === null) return;
    inFlight.current = true;
    setApplying(true);
    setApplyFailure(null);
    setApplyReference(null);

    try {
      const result = await centresImportApi.apply(fileText, preview.preview_token);
      setApplied(result);
      setPreview(result);
      // The write just happened, against exactly this `fileText`. Leaving
      // `previewedFileText` set to it would leave `applyDisabled` false —
      // Apply live beside "Import complete." for a file already applied. A
      // second click cannot double-write (the server's own verdicts all
      // read `unchanged` on a re-preview), but nothing on this screen should
      // invite a second click. Clearing it forces a fresh preview first.
      setPreviewedFileText(null);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setApplyFailure(describeFailure(error, t('reference.import.applyUnknown')));
      setApplyReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setApplying(false);
    }
  }

  function renderVerdict(row: Row): ReactNode {
    const verdict = (row as unknown as ImportRow).verdict;
    switch (verdict) {
      case 'create':
        return t('reference.import.verdictCreate');
      case 'update':
        return t('reference.import.verdictUpdate');
      case 'unchanged':
        return t('reference.import.verdictUnchanged');
      case 'reject':
        return t('reference.import.verdictReject');
      default:
        return verdict;
    }
  }

  // The whole point of task 8: a person confirming an import has to see WHAT
  // will change, not just which rows will. `changes` comes straight off the
  // API's own `ImportRow` — never recomputed here, so this table and the
  // server can never disagree about what an update touches.
  //
  // `create` and `reject` verdicts render nothing (the em-dash "not
  // recorded" cell); `reject` already has its own reason. An `update` whose
  // `changes` array is EMPTY is not the same thing as an absent list — that
  // is the no-op update the dry run still classified as `update` — so it
  // gets its own sentence rather than falling into that same blank cell.
  function renderChanges(row: Row): ReactNode {
    const importRow = row as unknown as ImportRow;
    if (importRow.verdict !== 'update') return null;
    const changes = importRow.changes ?? [];
    if (changes.length === 0) return t('reference.import.changesEmpty');
    // Field identifiers, not prose — isolated the same way `external_code`
    // is, so a comma-joined list of snake_case names never mirrors under RTL.
    return <bdi dir="ltr">{changes.join(', ')}</bdi>;
  }

  const columns: readonly Column[] = [
    { key: 'row_number', label: t('reference.import.columnRow'), numeric: true },
    { key: 'external_code', label: t('reference.import.columnCode'), neverMirrors: true },
    { key: 'verdict', label: t('reference.import.columnVerdict'), render: renderVerdict },
    { key: 'changes', label: t('reference.import.columnChanges'), render: renderChanges },
    { key: 'reason', label: t('reference.import.columnReason') },
  ];

  const verdictRows: readonly VerdictRow[] = preview === null ? [] : preview.rows.map(toRow);
  const status: ListStatus = verdictRows.length === 0 ? 'empty' : 'ready';
  // Distinct empty copy for "nothing chosen/previewed yet" versus "a dry run
  // ran and found zero rows" — both hit `status === 'empty'`, but only the
  // second one is a fact about the file.
  const emptyBody =
    preview === null ? t('reference.import.noPreviewYet') : t('reference.import.noRows');

  // Disabled until a preview exists AND matches the file now selected —
  // see the component docblock, point 3. Rendered unconditionally (never
  // absent) so this attribute is the one thing a mutant has to defeat.
  const applyDisabled =
    !canManage || preview === null || fileText !== previewedFileText || applying || previewing;

  return (
    <div className={styles['page']}>
      {/*
        One card in the design language (IconDisc head, accent wash). The
        file picker is the shared FileField, not the browser's own "Choose
        File / No file chosen" control.

        WHICH BUTTON IS PRIMARY FOLLOWS THE STEP. Apply (the upload icon) is
        disabled until a dry run of THIS file has been seen, and it used to be
        the only filled button on the row, so a file chosen and "Apply"
        clicked looked like an upload that did nothing. Until a matching
        preview exists, Preview is the primary action and Apply is secondary;
        after it, the two swap.
      */}
      <Card wash icon={<UploadSimpleIcon size={16} />} title={t('reference.import.title')}>
        <p className={styles['intro']}>{t('reference.import.intro')}</p>

        {canManage ? (
          <div className={styles['fileRow']}>
            <FileField
              ref={fileInputRef}
              id="referenceImportFile"
              label={t('reference.import.fileLabel')}
              accept=".csv,text/csv"
              fileName={fileName}
              emptyText={t('reference.import.noFileChosen')}
              onChange={(event) => {
                void handleFileChange(event);
              }}
            />

            <Button
              variant={applyDisabled ? 'primary' : 'secondary'}
              loading={previewing}
              disabled={fileText === null || previewing}
              onClick={() => {
                void handlePreview();
              }}
              icon={<EyeIcon size={16} />}
            >
              {t('reference.import.preview')}
            </Button>

            <Button
              variant={applyDisabled ? 'secondary' : 'primary'}
              loading={applying}
              disabled={applyDisabled}
              onClick={() => {
                void handleApply();
              }}
              icon={<UploadSimpleIcon size={16} />}
            >
              {t('reference.import.apply')}
            </Button>
          </div>
        ) : null}

        {preview === null ? null : (
          <p className={styles['counts']}>
            {t('reference.import.countsSummary', {
              create: preview.counts.create,
              update: preview.counts.update,
              unchanged: preview.counts.unchanged,
              reject: preview.counts.reject,
            })}
          </p>
        )}
      </Card>

      {/*
        The four live regions below are mounted unconditionally, on the
        highest-consequence surface in this screen — a non-sighted
        administrator must hear a preview result AND a preview failure, not
        just see the Apply button's disabled state change. A region born
        WITH its text already inside it gives the platform nothing to diff
        (`src/test/announcements.ts`'s own model) — the same reasoning
        `ReferenceLifecycleActions.tsx` and the form dialogs already apply
        to their own alerts.
      */}
      {renderFailure(previewFailure, previewReference, 'referenceImportPreviewRequestId')}

      {renderFailure(applyFailure, applyReference, 'referenceImportApplyRequestId')}

      <p className={styles['success']} role="status">
        {applied === null
          ? ''
          : [
              t('reference.import.applied'),
              t('reference.import.countsSummary', {
                create: applied.counts.create,
                update: applied.counts.update,
                unchanged: applied.counts.unchanged,
                reject: applied.counts.reject,
              }),
            ].join(' ')}
      </p>

      <ClientDataTable
        caption={t('reference.import.title')}
        columns={columns}
        rows={verdictRows}
        status={status}
        empty={{ body: emptyBody }}
      />
    </div>
  );
}
